/**
 * The distinction that matters throughout the app: an `error` means the
 * character is illegal and cannot be exported as finished. A `warning` means
 * it is legal but probably not what the player intended (unspent points, a
 * dump stat, a trait with no synergy). The UI blocks on the former and merely
 * annotates the latter.
 */
export type IssueSeverity = "error" | "warning";

export interface ValidationIssue {
  severity: IssueSeverity;
  /** Stable machine-readable identifier, e.g. "budget.overspent". */
  code: string;
  /** Player-facing sentence. Should say what to do, not just what is wrong. */
  message: string;
  /**
   * Path into the character document, so the UI can focus the offending
   * field, e.g. ["attributes", "resolve"].
   */
  path?: string[];
}

export interface ValidationResult {
  issues: ValidationIssue[];
}

export function isLegal(result: ValidationResult): boolean {
  return !result.issues.some((issue) => issue.severity === "error");
}

export function errorsOf(result: ValidationResult): ValidationIssue[] {
  return result.issues.filter((issue) => issue.severity === "error");
}

export function warningsOf(result: ValidationResult): ValidationIssue[] {
  return result.issues.filter((issue) => issue.severity === "warning");
}

export function mergeResults(...results: ValidationResult[]): ValidationResult {
  return { issues: results.flatMap((result) => result.issues) };
}

export const ok: ValidationResult = { issues: [] };
